import Model from '@newspaces/zero-base/Model';
import Organisation from './models/Organisation.js';
import { a as appendFilter, A as AccessSite } from './AccessSite-5abc043f.js';
import AccessTimeSchedule from './models/AccessTimeSchedule.js';
import { b as canUpdateDocuments, c as canInsertDocument, a as canRemoveDocuments } from './canRemoveDocuments-3b897618.js';
import { BlueOssSoDoorGroupId } from '@blueid/access-proto';

const normalizeFilter = (Runtime, filter, field) => {
  const organisationUser = Runtime.getOrganisationData(filter.organisation);
  if (!Runtime.userHasAllScopes(filter.organisation.toString(), null, "accessSite.read")) {
    return { filter, fields: [`-${field}`] };
  }
  const { customPermissions = {} } = organisationUser;
  const { accessSites = [] } = customPermissions;
  const finalFilter = filter;
  if (!accessSites?.length) {
    return { filter, fields: "all" };
  }
  const $in = {
    $in: accessSites
  };
  appendFilter(finalFilter, field, $in);
  return { filter: finalFilter, fields: "all" };
};

const queryDocument = (filter, _, Runtime) => {
  if (!filter.organisation) {
    return false;
  }
  const organisationUser = Runtime.getOrganisationData(filter.organisation);
  if (!organisationUser) {
    return false;
  }
  const hasScope = (scope) => {
    return Runtime.userHasAllScopes(
      filter.organisation.toString(),
      null,
      scope
    );
  };
  if (!hasScope("accessGroup.read")) {
    return false;
  }
  return normalizeFilter(Runtime, filter, "sites");
};

const editDocuments = async (filter, changeSet, _, Runtime) => {
  const { organisation: organisationId } = filter;
  if (!organisationId) {
    return false;
  }
  const hasScope = (scope) => {
    return Runtime.userHasAllScopes(
      filter.organisation.toString(),
      null,
      scope
    );
  };
  if (!hasScope("accessGroup.edit")) {
    return false;
  }
  return canUpdateDocuments(Runtime, filter, changeSet, "accessGroups", "sites");
};

async function canCreateGroup(db, organisation) {
  const lastGroup = await db.collection("accessGroups").findOne(
    {
      organisation,
      groupId: { $exists: true, $ne: null }
    },
    { sort: { groupId: -1 } }
  );
  const groupId = lastGroup ? lastGroup.groupId + 1 : 1;
  return groupId < BlueOssSoDoorGroupId.MasterGroupId;
}

const insertDocuments = async (documents, _, Runtime) => {
  const clearedDocuments = [];
  const hasScope = (organisationId, scope) => {
    return Runtime.userHasAllScopes(
      organisationId.toString(),
      null,
      scope
    );
  };
  const checkDocument = async (document) => {
    const { organisation: organisationId } = document;
    if (!organisationId) {
      return;
    }
    if (!hasScope(organisationId, "accessGroup.insert")) {
      return;
    }
    if (!await canCreateGroup(Runtime.db, organisationId)) {
      return;
    }
    if (!canInsertDocument(Runtime, document, "sites")) {
      return;
    }
    clearedDocuments.push(document);
  };
  await Promise.all(documents.map(checkDocument));
  if (!clearedDocuments.length) {
    return false;
  }
  return clearedDocuments;
};

const removeDocuments = async (filter, Runtime) => {
  const { organisation: organisationId } = filter;
  if (!organisationId) {
    return false;
  }
  if (!Runtime.userHasAllScopes(organisationId.toString(), null, "accessGroup.remove")) {
    return false;
  }
  return canRemoveDocuments(Runtime, filter, "accessGroups", "sites");
};

const insertDocumentsOwner = async (documents, _, Runtime) => {
  const clearedDocuments = [];
  const checkDocument = async (document) => {
    const { organisation: organisationId } = document;
    if (organisationId) {
      if (!await canCreateGroup(Runtime.db, organisationId)) {
        return;
      }
    }
    clearedDocuments.push(document);
  };
  await Promise.all(documents.map(checkDocument));
  if (!clearedDocuments.length) {
    return false;
  }
  return clearedDocuments;
};

const Permissions = {
  insert: [
    {
      who: "owner",
      query: insertDocumentsOwner
    },
    {
      query: insertDocuments
    }
  ],
  read: [
    {
      who: "owner"
    },
    {
      query: queryDocument
    }
  ],
  edit: [
    {
      who: "owner"
    },
    {
      query: editDocuments
    }
  ],
  remove: [
    {
      who: "owner"
    },
    {
      query: removeDocuments
    }
  ]
};

class AccessGroup extends Model {
  static CollectionName = "accessGroups";
  static Permissions = Permissions;
  static Fields = {
    organisation: {
      type: Organisation,
      serialize: "insert",
      required: true
    },
    sites: {
      type: [AccessSite],
      default: () => [],
      required: true,
      serialize: "insert",
      description: "The site(s) this group belongs too"
    },
    groupId: {
      type: Number,
      serialize: false,
      description: "The unique ID of this group. Automatically generated by the system",
      unique: "local"
    },
    name: {
      type: String,
      required: true,
      description: "The actual name of the group"
    },
    description: {
      type: String,
      description: "An optional description for the group"
    },
    accessSchedules: {
      type: [AccessTimeSchedule],
      validate: ({ accessSchedules }) => {
        if (accessSchedules?.length > 2) {
          return "maxTwoSchedules";
        }
        return null;
      },
      serialize: "insert",
      description: "Optional restriction by schedules for this group"
    }
  };
  constructor(data) {
    super(AccessGroup.Fields, data);
  }
}
Model.declareModel(AccessGroup);

export { AccessGroup as A, normalizeFilter as n };
//# sourceMappingURL=AccessGroup-4a811995.js.map
